/**
 * Hand-rolled inline-SVG chart primitives for the analytics dashboard (#24).
 *
 * No external charting library — same precedent as `AiUsagePage` (#19, see
 * `DailySpendSparkline`). Recharts/visx is too heavy for the simple shapes
 * we render and a third-party dep would balloon the admin bundle for what
 * fits in ~50 lines of SVG.
 *
 * Two primitives:
 *
 *   - `<DailyBarChart>` — vertical bars, one per day, scaled to the maximum
 *     value in the series. Shows the full daily-page-view timeline.
 *   - `<BotHumanRatioBar>` — a stacked horizontal bar (humans on the left,
 *     bots on the right). Visually communicates "what fraction of traffic
 *     was bot" without needing a real pie chart.
 *
 * Both render at `width: 100%` so the parent `<Paper>` controls layout.
 */

import type { ReactElement } from "react";
import { Box, Typography } from "@mui/material";

const PADDING = 4;
const HUMAN_COLOR = "#1976d2"; // MUI primary blue
const BOT_COLOR = "#9e9e9e"; // grey 500
const EMPTY_COLOR = "rgba(0, 0, 0, 0.08)";

export type DailyBarPoint = {
  date: string;
  value: number;
};

/**
 * Render a vertical bar chart inline. Empty series collapses to an empty-
 * state caption so the dashboard doesn't render a 200-px-tall blank area.
 */
export function DailyBarChart({
  data,
  width = 720,
  height = 160,
  testId,
}: {
  data: ReadonlyArray<DailyBarPoint>;
  width?: number;
  height?: number;
  testId?: string;
}): ReactElement {
  if (data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        <Typography variant="caption" color="text.secondary">
          No data in this range yet.
        </Typography>
      </Box>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.value), 0);
  const innerW = width - PADDING * 2;
  const innerH = height - PADDING * 2;
  // Bar width with a 2-px gap between bars. We allocate at least 1px per
  // bar to keep narrow ranges visible at small viewports.
  const slot = innerW / data.length;
  const barWidth = Math.max(1, slot - 2);

  return (
    <Box
      sx={{ width: "100%", overflow: "hidden" }}
      data-testid={testId}
      role="img"
      aria-label={`Daily page views, ${data.length} day${data.length === 1 ? "" : "s"}`}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
      >
        {data.map((point, i) => {
          const x = PADDING + i * slot + (slot - barWidth) / 2;
          const h = maxValue === 0 ? 0 : (point.value / maxValue) * innerH;
          const y = PADDING + innerH - h;
          return (
            <rect
              key={point.date}
              x={x}
              y={y}
              width={barWidth}
              height={Math.max(0, h)}
              fill={point.value > 0 ? HUMAN_COLOR : EMPTY_COLOR}
            >
              <title>{`${point.date}: ${point.value.toLocaleString()}`}</title>
            </rect>
          );
        })}
      </svg>
    </Box>
  );
}

/**
 * Stacked horizontal bar showing human vs bot view share. Renders the
 * percentage label for each segment when there's room (>=10% of the total).
 */
export function BotHumanRatioBar({
  humanViews,
  botViews,
  height = 32,
  testId,
}: {
  humanViews: number;
  botViews: number;
  height?: number;
  testId?: string;
}): ReactElement {
  const total = humanViews + botViews;
  if (total === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: EMPTY_COLOR,
          borderRadius: 1,
        }}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        <Typography variant="caption" color="text.secondary">
          No traffic in this range yet.
        </Typography>
      </Box>
    );
  }

  const humanRatio = humanViews / total;
  const botRatio = botViews / total;
  const showHumanLabel = humanRatio >= 0.1;
  const showBotLabel = botRatio >= 0.1;
  const humanPct = Math.round(humanRatio * 100);
  const botPct = Math.round(botRatio * 100);

  return (
    <Box sx={{ width: "100%" }} data-testid={testId}>
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        role="img"
        aria-label={`Human ${humanPct}%, Bot ${botPct}%`}
      >
        <rect x={0} y={0} width={humanRatio * 100} height={height} fill={HUMAN_COLOR} />
        <rect x={humanRatio * 100} y={0} width={botRatio * 100} height={height} fill={BOT_COLOR} />
        {showHumanLabel ? (
          <text
            x={(humanRatio * 100) / 2}
            y={height / 2 + 4}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={12}
          >
            {`Humans ${humanPct}%`}
          </text>
        ) : null}
        {showBotLabel ? (
          <text
            x={humanRatio * 100 + (botRatio * 100) / 2}
            y={height / 2 + 4}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={12}
          >
            {`Bots ${botPct}%`}
          </text>
        ) : null}
      </svg>
    </Box>
  );
}
